<!-- eslint-disable vue/no-v-html -->
<template>
    <VJoyPanel class="terms-and-conditions-panel">
        <template #panel-title>{{ $t('projectclient.quote.view.terms-and-conditions.title') }}</template>
        <template #panel-body>
            <VJoyWrapper
                v-if="quote.invoicingMode === 'TRIPARTITE' || quote.invoicingMode === 'SUBCONTRACTING'"
                row-gap="0"
                direction="column"
                align="flex-start"
            >
                <p class="terms-and-conditions-panel__section-title">
                    {{ $t('projectclient.quote.view.terms-and-conditions.up.model.title') }}
                </p>

                <p>
                    {{ $t('projectclient.quote.view.terms-and-conditions.up.model.description') }}
                </p>
            </VJoyWrapper>

            <VJoyWrapper v-if="quote.clientTerms?.acceptedDate" row-gap="1" direction="column" align="flex-start">
                <VJoyHighlight level="success" display-icon data-testid="quote-client-terms-accepted-highlight">
                    <span
                        v-html="
                            $t('projectclient.quote.view.terms-and-conditions.client.terms.freelancer.accepted', [
                                escapeHtml(quote.freelancer.firstName),
                                quote.clientTerms?.fileUrl,
                                formatDate(quote.clientTerms?.acceptedDate),
                            ])
                        "
                    />
                </VJoyHighlight>
            </VJoyWrapper>

            <VJoyWrapper v-if="quote.projectTerms" row-gap="1" direction="column" align="flex-start">
                <VJoyHighlight v-if="quote.projectTerms.clientAcceptedAt" level="success" display-icon>
                    <span
                        v-html="
                            $t('projectclient.quote.view.terms-and-conditions.project.terms.client.accepted', [
                                quote.projectTerms.url,
                                formatDate(quote.projectTerms.clientAcceptedAt),
                            ])
                        "
                    />
                </VJoyHighlight>

                <ValidationError v-else :errors="r$.$errors.acceptedProjectTerms" margin>
                    <VJoyCheckbox
                        v-model:checked="r$.$value.acceptedProjectTerms"
                        :invalid="r$.acceptedProjectTerms.$error"
                        data-testid="quote-project-terms-and-conditions"
                    >
                        <span
                            v-html="$t('projectclient.quote.view.terms-and-conditions.project.terms.client.not.accepted', [quote.projectTerms!.url])"
                        />
                    </VJoyCheckbox>
                </ValidationError>

                <VJoyHighlight v-if="quote.projectTerms.freelancerAcceptedAt" level="success" display-icon>
                    <span
                        v-html="
                            $t('projectclient.quote.view.terms-and-conditions.project.terms.freelancer.accepted', [
                                escapeHtml(quote.freelancer.firstName),
                                quote.projectTerms!.url,
                                formatDate(quote.projectTerms!.freelancerAcceptedAt),
                            ])
                        "
                    />
                </VJoyHighlight>
            </VJoyWrapper>

            <VJoyWrapper
                v-if="quote.invoicingMode === 'DIRECT' && quote.clientTerms?.fileUrl && quote.clientTerms.acceptedDate"
                row-gap="0"
                direction="column"
                align="flex-start"
            >
                <p class="terms-and-conditions-panel__section-title">
                    {{ $t('projectclient.quote.view.terms-and-conditions.each', [quote.client.company.name]) }}
                </p>
                <p
                    v-html="
                        $t('projectclient.quote.view.terms-and-conditions.link', {
                            0: escapeHtml(quote.freelancer.firstName),
                            1: escapeHtml(quote.clientTerms?.fileUrl),
                            2: escapeHtml(quote.client.company.name),
                            3: escapeHtml(formatDate(quote.clientTerms?.acceptedDate, {dateStyle: 'medium'})),
                        })
                    "
                ></p>
            </VJoyWrapper>

            <VJoyWrapper v-if="quote.freelancerTerms" row-gap="0" direction="column" align="flex-start">
                <p class="terms-and-conditions-panel__section-title">
                    {{ $t('projectclient.quote.view.terms-and-conditions.each', {0: escapeHtml(quote.freelancer.firstName)}) }}
                </p>
                <p>{{ quote.freelancerTerms }}</p>
            </VJoyWrapper>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {useFormat} from '#imports';
    import {escapeHtml} from '@malt/sanitize';
    import {VJoyWrapper, VJoyPanel, VJoyCheckbox, VJoyHighlight} from '@maltjoy/core-vue';
    import type {QuoteResource} from '@api';
    import ValidationError from '~/common/ui/ValidationError.vue';
    import {useClientQuoteViewStore} from '../../stores/quote-view.store';

    defineProps<{
        quote: QuoteResource;
    }>();

    const {r$} = useClientQuoteViewStore();
    const {formatDate} = useFormat();
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .terms-and-conditions-panel {
        &__section-title {
            @include text.getFontBySize(primary-450);

            margin-bottom: var(--joy-core-spacing-2);
        }
    }
</style>
